'use client';

import React from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import { PRODUCTS_PER_BRAND } from './constants';
import styles from './styles.module.css';
import type { PopularBrandItem } from './types';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ProductGridCard } from '@/components/ProductGridCard';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';

type Props = {
  brand: PopularBrandItem;
  brandIndex: number;
};

export function PopularBrandsClient({ brand, brandIndex }: Props) {
  const t = useTranslations('common');
  const { sendActivityTrackerEvent } = useActivityTracker();

  function handleShopClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: HomePageModuleNames.PopularBrands,
      itemClicked: brand.name,
      itemPosition: brandIndex,
      schemaVersion: '3.0',
    });
  }

  function handleProductImageClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: 'popular_brands_view_brand',
    });
  }

  const gridProducts = brand.products
    .slice(0, PRODUCTS_PER_BRAND)
    .map((p, index) => ({
      id: index,
      url: brand.url,
      imageSrc: p.imageSrc,
      width: p.width,
      height: p.height,
      alt: p.alt,
      ariaLabel: t('PopularBrands.ViewProductsFromBrand', {
        brandName: brand.name,
      }),
    }));

  return (
    <ProductGridCard
      products={gridProducts}
      productsListAriaLabel={`Products from ${brand.name}`}
      onProductLinkClick={handleProductImageClick}
    >
      <footer className={styles.footer}>
        <ExtendedLink
          href={brand.url}
          className={styles.bio}
          onClick={handleShopClick}
        >
          <Caption variant="lg-bold" className={styles.fullName} colour="base">
            {brand.name}
          </Caption>
        </ExtendedLink>
        <ExtendedLink href={brand.url} onClick={handleShopClick}>
          <ButtonExperiment as="span" size="small">
            {t('Shop')}
          </ButtonExperiment>
        </ExtendedLink>
      </footer>
    </ProductGridCard>
  );
}
